import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { FileCategory, INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IApi, useClientApi } from '@/setup/api-client'
import { useInfiniteApiQuery } from '@/utils/api/api-infinite-query'
import { useApiQuery } from '@/utils/api/api-query'
import { IApiResponse } from '@/utils/api/api-types'
import { InfiniteData, QueryKey } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { OrganizationGetDirectoriesParams } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/content'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { IBrandBaseParams, useApiToolbox } from '@/utils/api/use-api-toolbox'
import { objectEntries } from '@/utils/object-entries'

type IGetNodeContentApiPayload = OrganizationGetDirectoriesParams

export type INodeConnections = Pick<OrganizationGetDirectoriesParams, 'products' | 'labels' | 'brands'>

export type IGetNodeContentParams = IBrandBaseParams &
    Pick<
        OrganizationGetDirectoriesParams,
        | 'type'
        | 'search'
        | 'globalSearch'
        | 'fileCategories'
        | 'onlyFavorites'
        | 'page'
        | 'order'
        | 'products'
        | 'labels'
        | 'brands'
    > & {
        nodePath: string
    }

export type INodeTagFilter = { key: string; value: string }

export class GetNodeContentQuery {
    static getBaseKey(infinite: boolean) {
        return infinite ? 'infinite-node-query' : 'node-query'
    }

    static getConnectionsBaseKeys() {
        return {
            products: 'products',
            labels: 'labels',
            brands: 'brands',
        } satisfies Record<keyof INodeConnections, string>
    }

    static getConnectionsKeys(connections: INodeConnections) {
        if (Object.values(connections).every((value) => value?.length === 0)) return ''

        const parts: string[] = []
        objectEntries(connections).forEach(([key, value]) => {
            const baseKey = this.getConnectionsBaseKeys()[key]

            parts.push(baseKey)

            if (value?.length) parts.push(value.join(','))
        })

        return parts
    }

    static prepareParams(params: IGetNodeContentParams) {
        return {
            organizationId: params.organizationId,
            brandSlug: params.brandSlug,
            nodePath: params.nodePath,
            type: params.type,
            search: params.search,
            globalSearch: params.globalSearch ?? false,
            page: params.page,
            fileCategories: params.fileCategories,
            onlyFavorites: params.onlyFavorites ?? false,
            order: params.order,
            products: params.products,
            labels: params.labels,
            brands: params.brands,
        } satisfies IGetNodeContentParams
    }

    static getKey(rawParams: IGetNodeContentParams, infinite = false) {
        const { brandSlug, organizationId, ...params } = this.prepareParams(rawParams)

        const connections = {
            products: params.products,
            labels: params.labels,
            brands: params.brands,
        }

        const connectionKeys = this.getConnectionsKeys(connections)

        return [
            ...NODES_BASE_KEY,
            organizationId,
            brandSlug,
            'get-node-content',
            this.getBaseKey(infinite),
            ...connectionKeys,
            JSON.stringify(params),
        ]
    }

    static call(api: IApi, rawParams: IGetNodeContentParams) {
        const { organizationId, brandSlug, nodePath, ...bodyParams } = this.prepareParams(rawParams)

        return api
            .post<
                INode[],
                IGetNodeContentApiPayload,
                { page: number }
            >(`/organization/${organizationId}/brands/${brandSlug}/nodes/content`, { path: nodePath, ...bodyParams })
            .then((res) => {
                // The backend sorts all folders to the beginning, so that the frontend can rely on the index of the nodes for the node selection.
                return res
            })
    }
}

export const useGetNodeContentQuery = ({
    nodePath,
    searchParams,
    enabled = true,
}: {
    nodePath: string
    enabled?: boolean
    searchParams?: Omit<IGetNodeContentParams, 'nodePath' | 'organizationId' | 'brandSlug' | 'area'>
}) => {
    const { api, apiParams } = useApiToolbox()

    const params: IGetNodeContentParams = {
        organizationId: apiParams.organizationId,
        brandSlug: apiParams.brandSlug,
        nodePath,
        ...searchParams,
    }

    return useApiQuery({
        queryKey: GetNodeContentQuery.getKey(params),
        queryFn: () => GetNodeContentQuery.call(api, params).then((res) => res?.data),
        enabled: !!apiParams.organizationId && !!nodePath && enabled,
    })
}

export const useInfiniteGetNodeContentQueryParams = ({
    nodePath,
    searchParams,
}: {
    nodePath: string
    searchParams: Omit<IGetNodeContentParams, 'nodePath' | 'organizationId' | 'brandSlug'>
}) => {
    const { apiParams } = useApiToolbox()

    const apiQueryParams = useMemo(
        () => ({
            nodePath,
            brandSlug: apiParams.brandSlug,
            organizationId: apiParams.organizationId,
            ...searchParams,
        }),
        [nodePath, apiParams.brandSlug, apiParams.organizationId, searchParams]
    )

    return {
        queryKey: GetNodeContentQuery.getKey(apiQueryParams, true),
        apiQueryParams,
    }
}

export type IUseInfiniteGetNodeContentQueryParams = {
    nodePath: string
    searchParams: Omit<IGetNodeContentParams, 'nodePath' | 'organizationId' | 'brandSlug'>
    enabled?: boolean
}

export const useInfiniteGetNodeContentQuery = ({
    nodePath,
    searchParams,
    enabled = true,
}: IUseInfiniteGetNodeContentQueryParams) => {
    const api = useClientApi()
    const organizationId = useOrganizationId()

    const { queryKey, apiQueryParams } = useInfiniteGetNodeContentQueryParams({ nodePath, searchParams })

    return useInfiniteApiQuery<IApiResponse<INode[], { page: number }>, QueryKey, number>({
        queryKey,
        meta: { nodePath },

        initialPageParam: 1,
        placeholderData: (prevData, prevQuery) => (prevQuery?.meta?.nodePath === nodePath ? prevData : undefined),
        enabled: !!organizationId && enabled,

        queryFn: ({ pageParam = 1 }) => {
            return GetNodeContentQuery.call(api, { ...apiQueryParams, page: pageParam })
        },

        getNextPageParam: (lastPage) => {
            if (!lastPage) return 1

            const numLastPage = lastPage.meta?.page
            const PAGE_SIZE = 30

            // If the page has fewer items than the page size, we've reached the end
            if (lastPage.data.length < PAGE_SIZE) return null

            return numLastPage + 1
        },
    })
}

export type IInfiniteGetNodeContentQueryData = InfiniteData<IApiResponse<INode[]>>

export type IUseInfiniteGetNodeContentQueryMeta = {
    path: string
}

export const useGetProductPhotosQuery = (productUuid: string) => {
    const { api, apiParams } = useApiToolbox()

    const params: IGetNodeContentParams = useMemo(
        () => ({
            organizationId: apiParams.organizationId,
            brandSlug: apiParams.brandSlug,
            nodePath: `/`,
            fileCategories: [FileCategory.IMAGE],
            products: [productUuid],
            // A product's photos are wherever the user filed them. Without this the
            // backend restricts to the drive root, so anything in a folder is invisible.
            globalSearch: true,
            area: 'DRIVE',
        }),
        [apiParams.organizationId, apiParams.brandSlug, productUuid]
    )

    return useApiQuery({
        queryKey: GetNodeContentQuery.getKey(params),
        queryFn: () => GetNodeContentQuery.call(api, params),
        enabled: !!productUuid && !!apiParams.organizationId && !!apiParams.brandSlug,
    })
}
